import React, { useState, useEffect } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { Plus, X, Edit2, Trash2, Users, UserPlus, UserMinus } from 'lucide-react';

const diasLabels = { seg: 'Seg', ter: 'Ter', qua: 'Qua', qui: 'Qui', sex: 'Sex', sab: 'Sab', dom: 'Dom' };
const todosOsDias = Object.keys(diasLabels);
const emptyForm = { nome: '', professor: '', horarioInicio: '', horarioFim: '', diasSemana: [], capacidade: 10 };

export default function ClinicaPilatesPage() {
  const [turmas, setTurmas] = useState([]);
  const [equipe, setEquipe] = useState([]);
  const [pacientesPilates, setPacientesPilates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  // Modal alunos
  const [turmaAlunos, setTurmaAlunos] = useState(null);
  const [savingAluno, setSavingAluno] = useState(false);
  const [buscaAluno, setBuscaAluno] = useState('');

  useEffect(() => {
    Promise.all([
      api.get('/clinica/turmas'),
      api.get('/clinica/dashboard'),
      api.get('/clinica/meus-pacientes?tipo=pilates'),
    ]).then(([t, d, p]) => {
      setTurmas(t.data.data.filter(t => t.ativa));
      setEquipe(d.data.data.equipe || []);
      setPacientesPilates(p.data.data || []);
    }).catch(() => toast.error('Erro ao carregar'))
    .finally(() => setLoading(false));
  }, []);

  const abrirModal = (turma = null) => {
    setEditando(turma);
    setForm(turma ? {
      nome: turma.nome,
      professor: turma.professor?._id || '',
      horarioInicio: turma.horarioInicio,
      horarioFim: turma.horarioFim,
      diasSemana: turma.diasSemana,
      capacidade: turma.capacidade,
    } : emptyForm);
    setShowModal(true);
  };

  const toggleDia = (dia) => {
    setForm(f => ({
      ...f,
      diasSemana: f.diasSemana.includes(dia)
        ? f.diasSemana.filter(d => d !== dia)
        : [...f.diasSemana, dia]
    }));
  };

  const handleSalvar = async (e) => {
    e.preventDefault();
    if (!form.nome) return toast.error('Nome é obrigatório');
    setSaving(true);
    try {
      if (editando) {
        const { data } = await api.put(`/clinica/turmas/${editando._id}`, form);
        setTurmas(t => t.map(tt => tt._id === editando._id ? data.data : tt));
        toast.success('Turma atualizada!');
      } else {
        const { data } = await api.post('/clinica/turmas', form);
        setTurmas(t => [...t, data.data]);
        toast.success('Turma criada!');
      }
      setShowModal(false);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao salvar');
    } finally { setSaving(false); }
  };

  const handleDeletar = async (id) => {
    if (!window.confirm('Desativar esta turma?')) return;
    try {
      await api.delete(`/clinica/turmas/${id}`);
      setTurmas(t => t.filter(tt => tt._id !== id));
      toast.success('Turma desativada.');
    } catch { toast.error('Erro ao desativar'); }
  };

  const handleAdicionarAluno = async (turmaId, alunoId) => {
    setSavingAluno(true);
    try {
      const { data } = await api.post(`/clinica/turmas/${turmaId}/alunos`, { alunoId });
      setTurmas(t => t.map(tt => tt._id === turmaId ? { ...tt, alunos: data.data.alunos } : tt));
      setTurmaAlunos(prev => ({ ...prev, alunos: data.data.alunos }));
      toast.success('Aluno adicionado!');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao adicionar aluno');
    } finally { setSavingAluno(false); }
  };

  const handleRemoverAluno = async (turmaId, alunoId) => {
    setSavingAluno(true);
    try {
      await api.delete(`/clinica/turmas/${turmaId}/alunos/${alunoId}`);
      setTurmas(t => t.map(tt => tt._id === turmaId
        ? { ...tt, alunos: tt.alunos.filter(a => (a._id || a) !== alunoId) }
        : tt
      ));
      setTurmaAlunos(prev => ({
        ...prev,
        alunos: prev.alunos.filter(a => (a._id || a) !== alunoId)
      }));
      toast.success('Aluno removido.');
    } catch { toast.error('Erro ao remover aluno'); }
    finally { setSavingAluno(false); }
  };

  const alunosNaTurma = (turma) => {
    return (turma.alunos || []).map(a => typeof a === 'object' ? a._id : a);
  };

  if (loading) return <div className="p-8 text-center text-gray-500">Carregando...</div>;

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Turmas de Pilates</h1>
        <button onClick={() => abrirModal()}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-medium transition-colors">
          <Plus className="w-4 h-4" /> Nova Turma
        </button>
      </div>

      {turmas.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
          <p className="text-gray-400">Nenhuma turma ativa. Crie a primeira!</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {turmas.map(turma => (
            <div key={turma._id} className="bg-white rounded-2xl border border-gray-100 p-5 space-y-3">
              <div className="flex items-start justify-between">
                <h3 className="font-semibold text-gray-900">{turma.nome}</h3>
                <div className="flex gap-1">
                  <button onClick={() => abrirModal(turma)} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleDeletar(turma._id)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <p className="text-sm text-gray-500">
                {turma.professor?.nome || 'Sem professor'} · {turma.horarioInicio}–{turma.horarioFim}
              </p>
              <div className="flex gap-1 flex-wrap">
                {todosOsDias.map(d => (
                  <span key={d} className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                    turma.diasSemana.includes(d) ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-400'
                  }`}>{diasLabels[d]}</span>
                ))}
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5 text-sm text-gray-600">
                  <Users className="w-4 h-4 text-gray-400" />
                  {turma.alunos?.length || 0}/{turma.capacidade} alunos
                </div>
                <button
                  onClick={() => { setTurmaAlunos(turma); setBuscaAluno(''); }}
                  className="flex items-center gap-1 px-2.5 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg text-xs font-medium transition-colors"
                >
                  <UserPlus className="w-3.5 h-3.5" /> Gerenciar Alunos
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal turma */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6 relative max-h-[90vh] overflow-y-auto">
            <button onClick={() => setShowModal(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            <h2 className="text-lg font-bold text-gray-900 mb-5">{editando ? 'Editar Turma' : 'Nova Turma'}</h2>
            <form onSubmit={handleSalvar} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
                <input value={form.nome} onChange={e => setForm(f => ({ ...f, nome: e.target.value }))} required
                  className="w-full px-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-400 outline-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Professor</label>
                <select value={form.professor} onChange={e => setForm(f => ({ ...f, professor: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-400 outline-none">
                  <option value="">Selecione</option>
                  {equipe.map(f => <option key={f._id} value={f._id}>{f.nome}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Início</label>
                  <input type="time" value={form.horarioInicio} onChange={e => setForm(f => ({ ...f, horarioInicio: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-400 outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Fim</label>
                  <input type="time" value={form.horarioFim} onChange={e => setForm(f => ({ ...f, horarioFim: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-400 outline-none" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Dias da Semana</label>
                <div className="flex gap-2 flex-wrap">
                  {todosOsDias.map(d => (
                    <button key={d} type="button" onClick={() => toggleDia(d)}
                      className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                        form.diasSemana.includes(d) ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}>
                      {diasLabels[d]}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Capacidade</label>
                <input type="number" value={form.capacidade} min={1}
                  onChange={e => setForm(f => ({ ...f, capacidade: Number(e.target.value) }))}
                  className="w-full px-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-400 outline-none" />
              </div>
              <button type="submit" disabled={saving}
                className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white rounded-xl text-sm font-medium transition-colors">
                {saving ? 'Salvando...' : 'Salvar'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Modal gerenciar alunos */}
      {turmaAlunos && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md relative flex flex-col max-h-[85vh]">
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <div>
                <h3 className="text-lg font-bold text-gray-900">{turmaAlunos.nome}</h3>
                <p className="text-xs text-gray-400">{turmaAlunos.alunos?.length || 0}/{turmaAlunos.capacidade} alunos</p>
              </div>
              <button onClick={() => setTurmaAlunos(null)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>

            <div className="overflow-y-auto flex-1 p-6 space-y-4">
              {/* Alunos na turma */}
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Alunos na turma</p>
                {!turmaAlunos.alunos?.length ? (
                  <p className="text-sm text-gray-400">Nenhum aluno na turma.</p>
                ) : (
                  <ul className="space-y-2">
                    {turmaAlunos.alunos.map(aluno => {
                      const id = typeof aluno === 'object' ? aluno._id : aluno;
                      const paciente = pacientesPilates.find(p => p._id === id);
                      const nome = paciente?.nome || (typeof aluno === 'object' ? aluno.nome : id);
                      return (
                        <li key={id} className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-xl">
                          <div>
                            <span className="text-sm text-gray-800">{nome}</span>
                            {paciente?.diagnosisTitle && (
                              <p className="text-xs text-amber-600 font-medium mt-0.5">⚠ {paciente.diagnosisTitle}</p>
                            )}
                          </div>
                          <button
                            onClick={() => handleRemoverAluno(turmaAlunos._id, id)}
                            disabled={savingAluno}
                            className="flex items-center gap-1 px-2 py-1 text-red-500 hover:bg-red-50 rounded-lg text-xs font-medium transition-colors"
                          >
                            <UserMinus className="w-3.5 h-3.5" /> Remover
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>

              {/* Pacientes disponíveis para adicionar */}
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Adicionar aluno</p>
                <input
                  type="text"
                  placeholder="Buscar por nome ou telefone..."
                  value={buscaAluno}
                  onChange={e => setBuscaAluno(e.target.value)}
                  className="w-full mb-3 px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-300"
                />
                {pacientesPilates
                  .filter(p => !alunosNaTurma(turmaAlunos).includes(p._id))
                  .filter(p => {
                    const q = buscaAluno.toLowerCase();
                    return !q || p.nome?.toLowerCase().includes(q) || p.telefone?.includes(q);
                  }).length === 0 ? (
                  <p className="text-sm text-gray-400">
                    {buscaAluno ? 'Nenhum paciente encontrado.' : 'Todos os pacientes de pilates já estão na turma.'}
                  </p>
                ) : (
                  <ul className="space-y-2">
                    {pacientesPilates
                      .filter(p => !alunosNaTurma(turmaAlunos).includes(p._id))
                      .filter(p => {
                        const q = buscaAluno.toLowerCase();
                        return !q || p.nome?.toLowerCase().includes(q) || p.telefone?.includes(q);
                      })
                      .map(p => (
                        <li key={p._id} className="flex items-center justify-between px-3 py-2 border border-gray-100 rounded-xl hover:bg-gray-50">
                          <div>
                            <span className="text-sm text-gray-800">{p.nome}</span>
                            {p.telefone && <p className="text-xs text-gray-400">{p.telefone}</p>}
                          </div>
                          <button
                            onClick={() => handleAdicionarAluno(turmaAlunos._id, p._id)}
                            disabled={savingAluno || (turmaAlunos.alunos?.length || 0) >= turmaAlunos.capacidade}
                            className="flex items-center gap-1 px-2.5 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg text-xs font-medium transition-colors disabled:opacity-40"
                          >
                            <UserPlus className="w-3.5 h-3.5" /> Adicionar
                          </button>
                        </li>
                      ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}